import { useRef, useEffect } from 'react';
import { useGameStore } from '../store';
import type { Position } from '../types';
import { liqPriceFor } from '../utils/liquidation';
import { fmt } from '../utils/format';

const UP   = '#FF3B30';
const DOWN = '#1E88E5';
const GOLD = '#FFB800';
const GRID = '#2C2C2E';
const LABL = '#636366';

function draw(
  canvas: HTMLCanvasElement,
  prices: number[],
  pos: Position | null,
  cash: number,
) {
  const ctx = canvas.getContext('2d');
  if (!ctx || prices.length < 2) return;

  const W = canvas.width;
  const H = canvas.height;
  ctx.clearRect(0, 0, W, H);

  const lo0 = Math.min(...prices);
  const hi0 = Math.max(...prices);
  const pad = (hi0 - lo0) * 0.18 || lo0 * 0.05 || 500;
  const lo = lo0 - pad;
  const hi = hi0 + pad;

  const toX = (i: number) => (i / (prices.length - 1)) * W;
  const toY = (p: number) => H - ((p - lo) / (hi - lo)) * (H - 4) - 2;

  // Grid + price labels
  const LEVELS = 4;
  ctx.font = '9px -apple-system, sans-serif';
  ctx.textAlign = 'left';
  for (let i = 0; i <= LEVELS; i++) {
    const p = lo + (hi - lo) * (i / LEVELS);
    const y = toY(p);
    ctx.strokeStyle = GRID;
    ctx.lineWidth = 0.5;
    ctx.globalAlpha = 0.5;
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
    ctx.fillStyle = LABL;
    ctx.globalAlpha = 0.8;
    ctx.fillText(fmt(Math.round(p)), 4, y - 3);
  }

  // Entry price dashed (gold)
  if (pos) {
    const ey = toY(pos.entryPrice);
    ctx.save();
    ctx.strokeStyle = GOLD;
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 4]);
    ctx.globalAlpha = 0.85;
    ctx.beginPath(); ctx.moveTo(0, ey); ctx.lineTo(W, ey); ctx.stroke();
    ctx.restore();

    // Liquidation price dashed (red)
    const lp = liqPriceFor(pos, cash);
    if (lp !== null) {
      const ly = toY(lp);
      ctx.save();
      ctx.strokeStyle = UP;
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 5]);
      ctx.globalAlpha = 0.6;
      ctx.beginPath(); ctx.moveTo(0, ly); ctx.lineTo(W, ly); ctx.stroke();
      ctx.restore();
    }
  }

  // Price segments (red=up, blue=down)
  ctx.globalAlpha = 1;
  for (let i = 1; i < prices.length; i++) {
    ctx.strokeStyle = prices[i] >= prices[i - 1] ? UP : DOWN;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(toX(i - 1), toY(prices[i - 1]));
    ctx.lineTo(toX(i),     toY(prices[i]));
    ctx.stroke();
  }

  // Current price dot
  const last = prices.at(-1)!;
  const isUp = last >= prices[prices.length - 2];
  ctx.fillStyle = isUp ? UP : DOWN;
  ctx.beginPath();
  ctx.arc(toX(prices.length - 1), toY(last), 3.5, 0, Math.PI * 2);
  ctx.fill();
}

export default function Chart() {
  const ref  = useRef<HTMLCanvasElement>(null);
  const sym      = useGameStore((s) => s.sym);
  const prices   = useGameStore((s) => s.prices);
  const position = useGameStore((s) => s.position);
  const cash     = useGameStore((s) => s.cash);

  const pts = prices[sym];
  const cur  = pts.at(-1) ?? 10000;
  const base = pts[0] ?? cur;
  const changePct = ((cur - base) / base) * 100;
  const isUp = changePct >= 0;

  // Danger: survival ratio < 30%
  let danger = false;
  if (position) {
    const rawPct = (cur - position.entryPrice) / position.entryPrice;
    const posVal = position.amount * (1 + rawPct * position.leverage);
    const origin = cash + position.amount;
    if (origin > 0) danger = (cash + posVal) / origin < 0.3;
  }

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    canvas.width  = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    draw(canvas, pts, position, cash);
  });

  return (
    <div id="chart-container" className={danger ? 'danger' : undefined}>
      <canvas ref={ref} id="chart" />
      <div id="price-overlay">
        <div id="current-price" style={{ color: isUp ? 'var(--up)' : 'var(--down)' }}>
          {fmt(cur)}냥
        </div>
        <div id="price-change" style={{ color: isUp ? 'var(--up)' : 'var(--down)' }}>
          {isUp ? '▲' : '▼'} {Math.abs(changePct).toFixed(2)}%
        </div>
      </div>
    </div>
  );
}
